<template>
  <section class="page" data-testid="page-plugins">
    <header class="page-header">
      <div><h1>插件</h1><p>插件加载状态、运行数据和安全操作入口。</p></div>
      <div class="actions">
        <button class="button" :disabled="!plugins.length" @click="copyCurrentView">复制当前视图</button>
        <button class="button" :disabled="loading" @click="reload">重载插件</button>
        <button class="button primary" :disabled="loading" @click="() => load()">{{ loading ? "刷新中..." : "刷新" }}</button>
      </div>
    </header>
    <div class="panel warning-panel">
      <h2>本地受信任代码</h2>
      <p>插件来自仓库内的本地 Python 文件。启用、禁用或重载插件会执行本地代码；只启用你信任并已审查的插件。</p>
    </div>
    <div class="metrics-grid">
      <MetricCard label="插件" :value="plugins.length" />
      <MetricCard label="已启用" :value="enabledCount" />
      <MetricCard label="高/未知风险" :value="riskyCount" />
      <MetricCard label="运行失败" :value="failingCount" />
      <MetricCard label="声明权限" :value="permissionMetrics.total" />
      <MetricCard label="未知权限" :value="permissionMetrics.unknown" />
    </div>
    <p v-if="actionResult" class="save-result">{{ actionResult }}</p>
    <p v-if="error" class="error-text">{{ error }}</p>
    <div class="split">
      <div class="panel">
        <h2>插件列表</h2>
        <div class="toolbar">
          <button class="button" :class="{ primary: filter === 'all' }" @click="filter = 'all'">全部</button>
          <button class="button" :class="{ primary: filter === 'enabled' }" @click="filter = 'enabled'">已启用</button>
          <button class="button" :class="{ primary: filter === 'risk' }" @click="filter = 'risk'">高风险</button>
          <button class="button" :class="{ primary: filter === 'failing' }" @click="filter = 'failing'">失败</button>
        </div>
        <DataTable :columns="columns" :rows="filteredPlugins" row-key="name">
          <template #loaded="{ row }"><StatusBadge :status="row.loaded ? 'ok' : 'down'" /></template>
          <template #enabled="{ row }"><StatusBadge :status="row.enabled ? 'ok' : 'down'" /></template>
          <template #risk="{ row }">
            <span class="status-badge" :class="riskClass(row.permission_summary?.risk)">
              {{ riskLabel(row.permission_summary?.risk) }}
            </span>
          </template>
          <template #permissions="{ row }">
            <div class="permission-list">
              <span v-for="item in row.permissions || []" :key="item" :class="{ high: isHighRisk(row, item), unknown: isUnknown(row, item) }">
                {{ row.permission_summary?.labels?.[item] || item }}
              </span>
              <span v-if="!(row.permissions || []).length">-</span>
            </div>
          </template>
          <template #runtime="{ row }">
            <div class="runtime-cell">
              <strong>{{ row.runtime_stats?.executions || 0 }} 次</strong>
              <span :class="{ danger: row.runtime_stats?.failures }">失败 {{ row.runtime_stats?.failures || 0 }}</span>
              <span>{{ formatDuration(row.runtime_stats?.avg_duration_ms) }} avg</span>
            </div>
          </template>
          <template #last_error="{ row }">
            <span class="error-text compact">{{ row.runtime_stats?.last_error || "-" }}</span>
          </template>
          <template #actions="{ row }">
            <div class="row-actions">
              <button class="button small" :disabled="loading" @click="selectPlugin(row)">详情</button>
              <button class="button small" :disabled="loading" @click="showData(row.name)">数据</button>
              <button class="button small" :disabled="loading" @click="copyPlugin(row)">复制</button>
              <button class="button small" :disabled="loading" @click="toggle(row, !row.enabled)">{{ row.enabled ? "禁用" : "启用" }}</button>
            </div>
          </template>
        </DataTable>
      </div>
      <div>
        <div class="panel">
          <h2>插件详情</h2>
          <div v-if="selectedPlugin" class="plugin-detail">
            <div class="runtime-grid compact">
              <div><span>风险</span><strong>{{ riskLabel(selectedPlugin.permission_summary?.risk) }}</strong></div>
              <div><span>入口</span><strong>{{ selectedPlugin.entrypoint || "-" }}</strong></div>
              <div><span>依赖</span><strong>{{ (selectedPlugin.dependencies || []).join(", ") || "-" }}</strong></div>
              <div><span>运行</span><strong>{{ selectedPlugin.runtime_stats?.executions || 0 }} 次 / 失败 {{ selectedPlugin.runtime_stats?.failures || 0 }}</strong></div>
            </div>
            <div class="permission-list detail-permissions">
              <span v-for="item in selectedPlugin.permissions || []" :key="item" :class="{ high: isHighRisk(selectedPlugin, item), unknown: isUnknown(selectedPlugin, item) }">
                {{ selectedPlugin.permission_summary?.labels?.[item] || item }}
              </span>
              <span v-if="!(selectedPlugin.permissions || []).length">无声明权限</span>
            </div>
            <pre>{{ JSON.stringify(selectedPlugin, null, 2) }}</pre>
          </div>
          <div v-else class="empty">选择一个插件查看入口、权限和运行详情。</div>
        </div>
        <div class="panel">
          <h2>插件诊断</h2>
          <div class="diagnostic-list">
            <div v-for="item in diagnostics" :key="item.label" class="diagnostic-row" :class="item.level">
              <strong>{{ item.label }}</strong>
              <span>{{ item.detail }}</span>
            </div>
          </div>
        </div>
        <div class="panel">
          <h2>插件数据</h2>
          <pre>{{ pluginData || "选择插件查看数据" }}</pre>
          <pre v-if="reloadResult">{{ reloadResult }}</pre>
        </div>
        <div class="panel">
          <h2>黑话解释</h2>
          <div class="form-grid">
            <FormField label="插件名"><input v-model="actionPlugin" placeholder="jargon_miner" /></FormField>
            <FormField label="词"><input v-model="jargonWord" /></FormField>
          </div>
          <button class="button primary" :disabled="loading || !jargonWord.trim()" @click="explain">解释</button>
          <pre>{{ explanation }}</pre>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { api, postJson } from "../api/client";
import { useClipboardResult } from "../composables/useClipboardResult";
import { useToastStore } from "../stores/toast";
import DataTable from "../ui/DataTable.vue";
import FormField from "../ui/FormField.vue";
import MetricCard from "../ui/MetricCard.vue";
import StatusBadge from "../ui/StatusBadge.vue";

const toast = useToastStore();
type PluginPermissionSummary = {
  risk?: string;
  valid?: boolean;
  declared?: string[];
  unknown?: string[];
  high_risk?: string[];
  labels?: Record<string, string>;
};
type PluginRuntimeStats = {
  executions?: number;
  failures?: number;
  avg_duration_ms?: number;
  last_error?: string;
};
type PluginRow = {
  name: string;
  version?: string;
  loaded?: boolean;
  enabled?: boolean;
  description?: string;
  dependencies?: string[];
  permissions?: string[];
  permission_summary?: PluginPermissionSummary;
  entrypoint?: string;
  stats?: Record<string, any>;
  runtime_stats?: PluginRuntimeStats;
};

const plugins = ref<PluginRow[]>([]);
const selectedPlugin = ref<PluginRow | null>(null);
const pluginData = ref("");
const reloadResult = ref("");
const error = ref("");
const actionResult = ref("");
const { copyJson } = useClipboardResult(actionResult, "浏览器不允许复制，请手动选择插件信息");
const filter = ref<"all" | "enabled" | "risk" | "failing">("all");
const actionPlugin = ref("jargon_miner");
const jargonWord = ref("");
const explanation = ref("");
const loading = ref(false);
const columns = [
  { key: "name", label: "名称" },
  { key: "version", label: "版本" },
  { key: "loaded", label: "加载" },
  { key: "enabled", label: "启用" },
  { key: "risk", label: "风险" },
  { key: "permissions", label: "权限" },
  { key: "runtime", label: "运行" },
  { key: "last_error", label: "最近错误" },
  { key: "description", label: "描述" },
  { key: "actions", label: "操作" }
];
const enabledCount = computed(() => plugins.value.filter((plugin) => plugin.enabled).length);
const riskyCount = computed(() => plugins.value.filter((plugin) => ["high", "invalid"].includes(plugin.permission_summary?.risk || "")).length);
const failingCount = computed(() => plugins.value.filter((plugin) => Number(plugin.runtime_stats?.failures || 0) > 0).length);
const permissionMetrics = computed(() => {
  let total = 0;
  let unknown = 0;
  for (const plugin of plugins.value) {
    total += (plugin.permissions || []).length;
    unknown += (plugin.permission_summary?.unknown || []).length;
  }
  return { total, unknown };
});
const filteredPlugins = computed(() => {
  if (filter.value === "enabled") return plugins.value.filter((plugin) => plugin.enabled);
  if (filter.value === "risk") return plugins.value.filter((plugin) => ["high", "invalid"].includes(plugin.permission_summary?.risk || ""));
  if (filter.value === "failing") return plugins.value.filter((plugin) => Number(plugin.runtime_stats?.failures || 0) > 0);
  return plugins.value;
});
const diagnostics = computed(() => [
  {
    label: "启用边界",
    detail: enabledCount.value ? `${enabledCount.value} 个插件处于启用状态，重载前确认它们都来自受信任代码` : "当前没有启用插件",
    level: enabledCount.value ? "warning" : "ok"
  },
  {
    label: "权限风险",
    detail: riskyCount.value ? `${riskyCount.value} 个插件包含高风险或未知权限` : "未发现高风险或未知权限声明",
    level: riskyCount.value ? "error" : "ok"
  },
  {
    label: "运行稳定性",
    detail: failingCount.value ? `${failingCount.value} 个插件最近出现运行失败` : "当前插件运行统计无失败",
    level: failingCount.value ? "warning" : "ok"
  }
]);
async function load(announce = true) {
  loading.value = true;
  error.value = "";
  if (announce) actionResult.value = "";
  try {
    plugins.value = (await api<{ plugins: PluginRow[] }>("/api/plugins")).plugins || [];
    if (selectedPlugin.value && !plugins.value.some((plugin) => plugin.name === selectedPlugin.value?.name)) selectedPlugin.value = null;
    else if (!selectedPlugin.value) selectedPlugin.value = plugins.value[0] || null;
    else selectedPlugin.value = plugins.value.find((plugin) => plugin.name === selectedPlugin.value?.name) || selectedPlugin.value;
    if (announce) actionResult.value = `已加载 ${plugins.value.length} 个插件`;
  } catch (err) {
    error.value = err instanceof Error ? err.message : "加载插件失败";
    toast.push(error.value, "error");
  } finally {
    loading.value = false;
  }
}
async function reload() {
  loading.value = true;
  error.value = "";
  actionResult.value = "";
  try {
    const payload = await postJson<any>("/api/plugins/reload", {});
    reloadResult.value = JSON.stringify(payload, null, 2);
    actionResult.value = "插件已重载";
    toast.push("插件已重载");
    await load(false);
  } catch (err) {
    error.value = err instanceof Error ? err.message : "重载插件失败";
    toast.push(error.value, "error");
  } finally {
    loading.value = false;
  }
}
async function showData(name: string) {
  actionPlugin.value = name;
  selectedPlugin.value = plugins.value.find((plugin) => plugin.name === name) || selectedPlugin.value;
  loading.value = true;
  error.value = "";
  actionResult.value = "";
  try {
    const payload = await api(`/api/plugins/${encodeURIComponent(name)}/data`);
    pluginData.value = JSON.stringify(payload, null, 2);
    actionResult.value = `已读取 ${name} 数据`;
  } catch (err) {
    error.value = err instanceof Error ? err.message : "读取插件数据失败";
    toast.push(error.value, "error");
  } finally {
    loading.value = false;
  }
}
function selectPlugin(plugin: PluginRow) {
  selectedPlugin.value = plugin;
  actionPlugin.value = plugin.name;
}
async function copyCurrentView() {
  await copyJson({
    filter: filter.value,
    metrics: {
      total: plugins.value.length,
      enabled: enabledCount.value,
      risky: riskyCount.value,
      failing: failingCount.value,
      permissions: permissionMetrics.value
    },
    plugins: filteredPlugins.value
  }, "当前插件视图已复制");
}
async function copyPlugin(plugin: PluginRow) {
  await copyJson(plugin, `${plugin.name} 已复制`);
}
function riskLabel(risk?: string) {
  const labels: Record<string, string> = { low: "低", medium: "中", high: "高", invalid: "未知" };
  return labels[risk || ""] || "-";
}
function riskClass(risk?: string) {
  if (risk === "high" || risk === "invalid") return "error";
  if (risk === "medium") return "degraded";
  return "ok";
}
function isHighRisk(plugin: PluginRow, permission: string) {
  return (plugin.permission_summary?.high_risk || []).includes(permission);
}
function isUnknown(plugin: PluginRow, permission: string) {
  return (plugin.permission_summary?.unknown || []).includes(permission);
}
function formatDuration(value: unknown) {
  const n = Number(value || 0);
  return Number.isFinite(n) ? `${Math.round(n)}ms` : "-";
}
function confirmEnable(plugin: PluginRow) {
  const risk = plugin.permission_summary?.risk || "low";
  if (!["high", "invalid"].includes(risk)) return true;
  const label = risk === "invalid" ? "包含未知权限" : "包含高风险权限";
  return window.confirm(`插件 ${plugin.name} ${label}，启用前请确认已审查代码。是否继续？`);
}
async function toggle(plugin: PluginRow, enabled: boolean) {
  if (enabled && !confirmEnable(plugin)) return;
  loading.value = true;
  error.value = "";
  actionResult.value = "";
  try {
    const name = plugin.name;
    await postJson(`/api/plugins/${encodeURIComponent(name)}/action`, { action: enabled ? "enable" : "disable" });
    actionResult.value = enabled ? `${name} 已启用` : `${name} 已禁用`;
    toast.push(enabled ? "插件已启用" : "插件已禁用");
    await load(false);
  } catch (err) {
    error.value = err instanceof Error ? err.message : "更新插件状态失败";
    toast.push(error.value, "error");
  } finally {
    loading.value = false;
  }
}
async function explain() {
  loading.value = true;
  error.value = "";
  actionResult.value = "";
  try {
    const payload = await postJson<any>(`/api/plugins/${encodeURIComponent(actionPlugin.value)}/action`, { action: "explain", word: jargonWord.value });
    explanation.value = payload.explanation || JSON.stringify(payload, null, 2);
    actionResult.value = "插件动作已执行";
  } catch (err) {
    error.value = err instanceof Error ? err.message : "插件动作失败";
    toast.push(error.value, "error");
  } finally {
    loading.value = false;
  }
}
onMounted(load);
</script>

<style scoped>
.warning-panel { border-color: #78612d; background: #241f14; }
.permission-list { display: flex; gap: 6px; flex-wrap: wrap; }
.permission-list span { border: 1px solid var(--border); border-radius: 999px; padding: 2px 7px; color: var(--muted); font-size: 12px; }
.permission-list span.high { border-color: #78612d; color: var(--warning); }
.permission-list span.unknown { border-color: #743434; color: var(--danger); }
.detail-permissions { margin-bottom: 12px; }
.runtime-cell { display: grid; gap: 3px; min-width: 82px; }
.runtime-cell span { color: var(--muted); font-size: 12px; }
.runtime-cell span.danger, .error-text.compact { color: var(--danger); }
.error-text.compact { font-size: 12px; }
.plugin-detail {
  display: grid;
  gap: 12px;
}
</style>
